import { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, Download, Sparkles } from 'lucide-react';
import { I, Modal, Skeleton, Spinner, ErrorCard } from './ui';
import { Combobox, type Option } from './Combobox';
import { Findings } from './AgentCards';
import { useApi } from '../lib/hooks';
import { useApp } from '../lib/store';
import { currentUserId, isPublicDemo } from '../lib/users';
import { date } from '../lib/format';
import { assertCompleteDownload } from '../lib/downloadCheck';
import '../styles/revision.css';

/** GET /api/t/:tid/export/revision: what the ZIP will contain (server/src/domain/revision.ts, revisionSummary). */
interface Chain { ok: boolean; count: number; head: string | null; brokenAt: number | null; reason: string | null }
interface Summary {
  period: { from: string; to: string; year: number | null; label: string; fyStart?: string | null }; running: boolean;
  entries: number; lines: number; firstSeq: number | null; lastSeq: number | null; documents: number; documentBytes: number; auditEvents: number; estimate: number;
  journal: Chain; audit: Chain; missing: unknown[]; mismatched: unknown[]; ok: boolean; findings: string[];
  /** Fiscal years by their first day (fyStart): after a change of the start two begin in one calendar year (OR-05). */
  years: { year: number; label: string; from: string; to: string; closed: boolean; fyStart: string }[]; defaultYear: string | number;
}

const CUSTOM = 'custom';
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const n0 = (n: number) => n.toLocaleString('de-CH');
const bytes = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
const shortDate = (iso: string) => `${Number(iso.slice(8, 10))}.${Number(iso.slice(5, 7))}.${iso.slice(0, 4)}`;

/** «Revisionsexport» on the Belege page: opens the period dialog. */
export function RevisionExportButton({ tid }: { tid: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn" onClick={() => setOpen(true)} title="Prüfpaket für die Revisionsstelle als ZIP (GeBüV)"><I icon={Archive} sm /> Revisionsexport</button>
      {open && <RevisionExportDialog tid={tid} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Pick the period (default: the last closed fiscal year), see what the package will hold and whether the chains are
 * intact, then download it, or hand it to Command as a draft. Keyboard: the picker is focused; Space or ↓ opens it,
 * Enter downloads.
 */
export function RevisionExportDialog({ tid, onClose }: { tid: string; onClose: () => void }) {
  const app = useApp();
  const [sel, setSel] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [years, setYears] = useState<Summary['years']>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const running = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const query = sel === CUSTOM ? (ISO.test(from) && ISO.test(to) ? `from=${from}&to=${to}` : null) : sel ? `year=${sel}` : '';
  const { data, error, loading } = useApi<Summary>(query == null ? null : `/api/t/${tid}/export/revision${query ? `?${query}` : ''}`);
  useEffect(() => {
    if (!data) return;
    if (!years.length) setYears(data.years);
    if (!sel) setSel(String(data.defaultYear));
  }, [data, years.length, sel]);
  useEffect(() => { setFailed(null); }, [query]);
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const options: Option[] = useMemo(() => [
    ...years.map((y) => ({ value: y.fyStart ?? y.from, label: y.label, secondary: `${date(y.from)} – ${date(y.to)} · ${y.closed ? 'abgeschlossen' : 'laufend'}`, keywords: String(y.year) })),
    { value: CUSTOM, label: 'Eigener Zeitraum…', secondary: 'Beginn und Ende frei wählen', keywords: 'von bis datum zeitraum' },
  ], [years]);
  const choose = (v: string) => {
    if (v === CUSTOM && data) { setFrom(data.period.from); setTo(data.period.to); }
    setSel(v);
  };

  const s = data && !loading ? data : null;
  const empty = !!s && !s.entries && !s.documents;
  const ready = !!s && !empty && query != null && !busy;

  const download = async () => {
    if (!ready || running.current) return;
    running.current = true;
    setBusy(true); setFailed(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`/api/t/${tid}/export/revision.zip?${query || (s!.period.fyStart ? `year=${s!.period.fyStart}` : `from=${s!.period.from}&to=${s!.period.to}`)}`, { signal: ctrl.signal, headers: isPublicDemo() ? { 'x-kontor-user': currentUserId() } : {} });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? `Export fehlgeschlagen (${res.status})`);
      const buf = await res.arrayBuffer();
      if (ctrl.signal.aborted) return;
      await assertCompleteDownload(buf, res.headers.get('content-length'), res.headers.get('x-kontor-sha256'));
      if (ctrl.signal.aborted) return;
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'Revisionsexport.zip';
      const href = URL.createObjectURL(new Blob([buf], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
      app.toast(`${name} heruntergeladen. Der Export steht in der Prüfspur und im Zugriffsprotokoll.`);
      onClose();
    } catch (e) {
      if (ctrl.signal.aborted || (e as Error).name === 'AbortError') return;
      setFailed((e as Error).message);
    } finally {
      running.current = false;
      if (!ctrl.signal.aborted) setBusy(false);
    }
  };
  const draft = () => {
    if (!s) return;
    app.openCommand(s.period.year != null && !s.period.label.startsWith('Rumpf') ? `Revisionsexport ${s.period.year}` : `Revisionsexport ${shortDate(s.period.from)}–${shortDate(s.period.to)}`, true);
    onClose();
  };
  // Enter downloads from the picker (closed) and the date fields; the open picker and real buttons keep their own Enter.
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.defaultPrevented || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) return;
    const t = e.target as HTMLElement;
    if (t.closest('.popover') || (t.tagName === 'BUTTON' && !t.hasAttribute('data-combo-trigger'))) return;
    e.preventDefault();
    download();
  };

  return (
    <Modal title="Revisionsexport" onClose={onClose} className="rev-modal" footer={<>
      <button className="btn ghost" onClick={draft} disabled={!s || empty} title="Als Entwurf in Command öffnen (⌘J)"><I icon={Sparkles} sm /> In Command</button>
      <button className="btn" onClick={onClose}>Abbrechen</button>
      <button className="btn primary" onClick={download} disabled={!ready} aria-busy={busy} aria-keyshortcuts="Enter">{busy ? <Spinner /> : <I icon={Download} sm />} {busy ? 'Export läuft' : 'ZIP herunterladen'}</button>
    </>}>
      <div className="card-pad stack rev-body" onKeyDown={onKey}>
        <p className="muted">Das Prüfpaket für die Revisionsstelle: Journal, Kontenplan, Probebilanz, Prüfspur und alle Originalbelege, mit Integritätsprüfung und SHA-256 je Datei (GeBüV Art. 6–9).</p>
        <div className="field">
          <span>Zeitraum</span>
          <Combobox value={sel || null} options={options} onChange={choose} ariaLabel="Zeitraum" placeholder="Wird geladen…" searchPlaceholder="Jahr suchen…" width="100%" />
        </div>
        {sel === CUSTOM && (
          <div className="rev-range">
            <label className="field"><span>Beginn</span><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="field"><span>Ende</span><input className="input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></label>
          </div>
        )}

        {error ? <ErrorCard message={error} /> : !s ? (
          query == null ? <p className="dim">Beginn und Ende wählen.</p> : <div className="stack-sm" aria-busy="true"><Skeleton h={14} /><Skeleton h={14} w="80%" /><Skeleton h={14} w="60%" /></div>
        ) : (
          <>
            <table className="data compact kv rev-summary" data-copy-surface aria-label="Inhalt des Exports">
              <tbody>
                <tr><td className="k" data-no-copy>Zeitraum</td><td>{date(s.period.from)} – {date(s.period.to)}{s.running && <span className="dim"> · läuft noch</span>}</td></tr>
                <tr><td className="k" data-no-copy>Buchungen</td><td>{s.entries ? `${n0(s.entries)} (Nr. ${s.firstSeq} bis ${s.lastSeq})` : 'keine'}</td></tr>
                <tr><td className="k" data-no-copy>Belege</td><td>{n0(s.documents)} Originaldateien · {bytes(s.documentBytes)}</td></tr>
                <tr><td className="k" data-no-copy>Prüfspur</td><td className="num">{n0(s.auditEvents)} Ereignisse</td></tr>
                <tr><td className="k" data-no-copy>Journal-Kette</td><td className={s.journal.ok ? '' : 'neg'}>{s.journal.ok ? 'intakt' : `unterbrochen ab Nr. ${s.journal.brokenAt}`}</td></tr>
                <tr><td className="k" data-no-copy>Prüfspur-Kette</td><td className={s.audit.ok ? '' : 'neg'}>{s.audit.ok ? 'intakt' : `unterbrochen ab Ereignis ${s.audit.brokenAt}`}</td></tr>
                <tr><td className="k" data-no-copy>Belege-Hashes</td><td className={s.missing.length || s.mismatched.length ? 'neg' : ''}>{!s.missing.length && !s.mismatched.length ? 'alle stimmen' : [s.mismatched.length && `${s.mismatched.length} abweichend`, s.missing.length && `${s.missing.length} fehlen`].filter(Boolean).join(', ')}</td></tr>
                <tr><td className="k" data-no-copy>Grösse</td><td className="num">ca. {bytes(s.estimate)}</td></tr>
              </tbody>
            </table>
            <Findings findings={s.findings.map((message) => ({ severity: 'error', message: `${message} Der Export wird trotzdem erstellt; pruefung.json hält es für die Revisionsstelle fest.` }))} />
            {empty && <p className="dim">Im Zeitraum gibt es weder Buchungen noch Belege.</p>}
            {s.running && !empty && <p className="dim rev-note">Der Zeitraum läuft noch: das ZIP zeigt den Stand beim Herunterladen.</p>}
          </>
        )}
        {failed && <ErrorCard message={failed} />}
        <p className="dim rev-note">Der Download steht mit deinem Namen in der Prüfspur und im Zugriffsprotokoll.</p>
      </div>
    </Modal>
  );
}
